// [MODALES-ACCES] BoutiqueOSWelcome — Landing page + modales connexion
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { applySeo } from "../lib/seo";

// [MODALES-ACCES] Helper: initiales depuis un nom
const getInitials = (name) => {
  if (!name) return "B";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export default function BoutiqueOSWelcome() {
  const navigate = useNavigate();
  const { login, setTeamAuth } = useAuth();

  // [MODALES-ACCES] États React
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Login modal states
  const [identifiant, setIdentifiant] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [shakeLogin, setShakeLogin] = useState(false);
  // Multi-step login: "search" → "role" → "form"
  const [loginStep, setLoginStep] = useState("search");
  const [selectedRole, setSelectedRole] = useState(null); // "owner" | "team"
  const [teamEmail, setTeamEmail] = useState("");

  // Cliente modal states
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [codeResult, setCodeResult] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [codeNotAvailable, setCodeNotAvailable] = useState(false);

  // Onboarding cliente states
  const [showClienteOnboarding, setShowClienteOnboarding] = useState(false);
  const [clientePrenom, setClientePrenom] = useState("");
  const [clienteTel, setClienteTel] = useState("");
  const [onboardingError, setOnboardingError] = useState("");
  const pendingBoutiqueRef = useRef(null);

  const debounceRef = useRef(null);
  const codeRefs = useRef([null, null, null, null, null, null]);

  // [SEO] Landing page metadata + Organization / SoftwareApplication JSON-LD
  useEffect(() => {
    const origin = window.location.origin;
    applySeo({
      title: "BoutiqueOS — Caisse, stock et vitrine WhatsApp pour boutiques et restaurants",
      description: "Logiciel de caisse et de gestion de stock pour boutiques de mode, restaurants, bars, épiceries et caves en Afrique francophone. Ventes en temps réel, équipe, vitrine partageable sur WhatsApp.",
      keywords: ["logiciel de caisse", "caisse enregistreuse", "gestion de stock", "boutique en ligne", "point de vente", "POS Afrique", "Abidjan", "Dakar", "vitrine WhatsApp", "restaurant", "boutique mode", "SaaS commerce"],
      canonical: `${origin}/`,
      image: `${origin}/icon-512.png`,
      jsonLdId: "landing",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "Organization", name: "BoutiqueOS", url: `${origin}/`, logo: `${origin}/icon-512.png` },
          { "@type": "SoftwareApplication", name: "BoutiqueOS", applicationCategory: "BusinessApplication", operatingSystem: "Web",
            description: "Caisse tactile, stock, équipe et vitrine WhatsApp pour commerces francophones.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "Essai gratuit" } },
          { "@type": "WebSite", name: "BoutiqueOS", url: `${origin}/`, inLanguage: "fr" },
        ],
      },
    });
  }, []);

  // [REDESIGN-LANDING] Load Inter + JetBrains Mono
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // [MODALES-ACCES] Debounced boutique check (login modal)
  useEffect(() => {
    if (!identifiant || identifiant.length < 2) {
      setCheckResult(null);
      setLoginError("");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    setChecking(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/check?q=${encodeURIComponent(identifiant.trim())}`);
        setCheckResult(res.data);
      } catch {
        setCheckResult({ found: false });
      } finally {
        setChecking(false);
      }
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [identifiant]);

  // Reset login modal on close
  useEffect(() => {
    if (!showLoginModal) {
      setIdentifiant("");
      setCheckResult(null);
      setPassword("");
      setShowPassword(false);
      setLoginError("");
      setShakeLogin(false);
      setLoginStep("search");
      setSelectedRole(null);
      setTeamEmail("");
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [showLoginModal]);

  // [MODALES-ACCES] Auto-focus first code box when cliente modal opens
  useEffect(() => {
    if (showClienteModal) {
      setCodeDigits(["", "", "", "", "", ""]);
      setCodeResult(null);
      setCodeInvalid(false);
      setCodeNotAvailable(false);
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    }
  }, [showClienteModal]);

  // [MODALES-ACCES] Lookup boutique by code
  const checkCode = async (code) => {
    setCodeLoading(true);
    setCodeResult(null);
    setCodeInvalid(false);
    setCodeNotAvailable(false);
    try {
      const res = await axios.get(`${API}/boutiques/code/${code}`);
      setCodeResult(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "";
      if (status === 403 || detail.includes("disponible")) {
        setCodeNotAvailable(true);
      } else {
        setCodeInvalid(true);
        setTimeout(() => {
          setCodeDigits(["", "", "", "", "", ""]);
          setCodeInvalid(false);
          codeRefs.current[0]?.focus();
        }, 1200);
      }
    } finally {
      setCodeLoading(false);
    }
  };

  // [MODALES-ACCES] Code input handlers
  const handleCodeChange = (index, value) => {
    const char = value.replace(/[^A-Za-z0-9]/g, '').slice(-1).toUpperCase();
    const newDigits = [...codeDigits];
    newDigits[index] = char;
    setCodeDigits(newDigits);
    setCodeResult(null);
    setCodeInvalid(false);
    setCodeNotAvailable(false);
    if (char && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
    if (newDigits.every(d => d !== '')) {
      checkCode(newDigits.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (codeDigits[index]) {
        const newDigits = [...codeDigits];
        newDigits[index] = '';
        setCodeDigits(newDigits);
      } else if (index > 0) {
        codeRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      codeRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      codeRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      const code = codeDigits.join('');
      if (code.length === 6) checkCode(code);
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^A-Za-z0-9]/g, '').slice(0, 6).toUpperCase();
    const newDigits = ["", "", "", "", "", ""];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setCodeDigits(newDigits);
    setCodeResult(null);
    setCodeInvalid(false);
    setCodeNotAvailable(false);
    if (pasted.length === 6) {
      checkCode(pasted);
    } else {
      codeRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  // [MODALES-ACCES] Login handler
  const triggerShake = () => {
    setShakeLogin(true);
    setTimeout(() => setShakeLogin(false), 600);
  };

  const handleLogin = async () => {
    if (selectedRole === "owner") {
      // Owner login: slug + password
      if (!password) {
        setLoginError("Entrez votre mot de passe");
        triggerShake();
        return;
      }
      setLoginLoading(true);
      setLoginError("");
      try {
        const slug = checkResult?.slug || identifiant.trim();
        const response = await login(slug, password);
        if (response?.boutique) localStorage.setItem("boutique", JSON.stringify(response.boutique));
        const compteValide = response?.compteValide !== false && response?.boutique?.compteValide !== false;
        sessionStorage.setItem("compteValide", String(compteValide));
        sessionStorage.setItem("currentUser", JSON.stringify({
          role: "owner",
          prenom: response?.user?.name?.split(' ')[0] || "Admin",
          email: response?.user?.email || "",
          id: response?.user?.id || "owner"
        }));
        localStorage.setItem("userRole", "owner");
        toast.success("Connexion réussie !");
        setShowLoginModal(false);
        navigate(compteValide ? "/admin/dashboard" : "/admin/sales");
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        if (detail === "ACCOUNT_NOT_VALIDATED" || detail.includes("non valid")) {
          setShowLoginModal(false);
          navigate("/admin/sales");
        } else if (detail.includes("désactivé") || detail === "ACCOUNT_DISABLED") {
          setLoginError("Ce compte est désactivé. Contactez le support.");
        } else {
          setLoginError("Mot de passe incorrect");
          triggerShake();
        }
      } finally { setLoginLoading(false); }
    } else if (selectedRole === "team") {
      // Team login: email + password + boutique_slug
      if (!teamEmail || !password) {
        setLoginError("Remplissez tous les champs");
        triggerShake();
        return;
      }
      setLoginLoading(true);
      setLoginError("");
      try {
        const slug = checkResult?.slug || identifiant.trim();
        const teamRes = await axios.post(`${API}/team/login`, {
          email: teamEmail.toLowerCase().trim(),
          password,
          boutique_slug: slug
        });
        const { access_token, member, boutique, is_first_login, compteValide } = teamRes.data;
        if (access_token) setTeamAuth(access_token, member);
        if (boutique) localStorage.setItem("boutique", JSON.stringify(boutique));
        sessionStorage.setItem("compteValide", String(compteValide !== false));
        sessionStorage.setItem("currentUser", JSON.stringify({
          role: member.role, prenom: member.prenom, email: member.email, id: member.id
        }));
        localStorage.setItem("userRole", member.role);
        toast.success(`Bienvenue ${member.prenom} !`);
        setShowLoginModal(false);
        if (member.role === "vendeur") navigate("/admin/sales");
        else if (member.role === "serveur") navigate("/admin/service");
        else navigate("/admin/dashboard");
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        if (detail.includes("désactivé")) {
          setLoginError("Votre compte a été désactivé. Contactez la gérante.");
        } else {
          setLoginError("Email ou mot de passe incorrect");
          triggerShake();
        }
      } finally { setLoginLoading(false); }
    }
  };

  // [MODALES-ACCES] Navigate to boutique vitrine — show onboarding first
  const handleClienteAccess = () => {
    if (!codeResult) return;
    pendingBoutiqueRef.current = codeResult;
    setShowClienteModal(false);
    setClientePrenom("");
    setClienteTel("");
    setOnboardingError("");
    setShowClienteOnboarding(true);
  };

  const handleClienteOnboardingSubmit = () => {
    if (!clientePrenom.trim()) {
      setOnboardingError("Veuillez entrer votre prénom");
      return;
    }
    const boutique = pendingBoutiqueRef.current;
    if (!boutique) return;
    sessionStorage.setItem(`cliente_name_${boutique.admin_id}`, clientePrenom.trim());
    if (clienteTel.trim()) sessionStorage.setItem(`cliente_phone_${boutique.admin_id}`, clienteTel.trim());
    setShowClienteOnboarding(false);
    navigate(`/vitrine/${boutique.admin_id}`);
  };

  return (
    <div className="boutiqueos-landing" data-testid="boutiqueos-welcome">
      {/* Background Video */}
      <video className="bg-video" autoPlay muted loop playsInline preload="auto">
        <source src="/video_bg.webm" type="video/webm" />
        <source src="/video_bg_web.mp4" type="video/mp4" />
      </video>
      <div className="bg-overlay" />
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-pill">

          {/* 1. LEFT — logo */}
          <div className="navbar-left">
            <div className="navbar-logo" onClick={() => navigate("/")}>
              BoutiqueOS
            </div>
          </div>

          {/* 2. CENTER — nav links */}
          <div className="navbar-links">
            <a href="#fonctionnalites" className="nav-link nav-link-soon" data-soon="Bientôt">Fonctionnalités</a>
            <a href="#tarifs" className="nav-link nav-link-soon" data-soon="Bientôt">Tarifs</a>
            <a href="#temoignages" className="nav-link nav-link-soon" data-soon="Bientôt">Témoignages</a>
          </div>

          {/* 3. RIGHT — action buttons */}
          <div className="navbar-actions">
            {/* Accès cliente */}
            <button
              className="navbar-cliente-btn"
              onClick={() => setShowClienteModal(true)}
              data-testid="acces-cliente-btn"
            >
              🛍️ Accès cliente
            </button>

            {/* Se connecter */}
            <button
              className="navbar-login-btn"
              onClick={() => setShowLoginModal(true)}
              data-testid="se-connecter-btn"
            >
              Se connecter
            </button>
          </div>

          {/* Hamburger — mobile only */}
          <button
            className="navbar-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
            data-testid="hamburger-btn"
          >
            <span className={`ham-bar ${menuOpen ? 'open-1' : ''}`} />
            <span className={`ham-bar ${menuOpen ? 'open-2' : ''}`} />
            <span className={`ham-bar ${menuOpen ? 'open-3' : ''}`} />
          </button>

        </div>
      </nav>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        >
          <div
            className="mobile-menu-panel"
            onClick={e => e.stopPropagation()}
          >
            <nav className="mobile-menu-links">
              <a href="#fonctionnalites" className="mobile-nav-link" onClick={() => setMenuOpen(false)}>Fonctionnalités <span style={{fontSize:'10px',color:'rgba(255,255,255,0.3)',marginLeft:'6px'}}>bientôt</span></a>
              <a href="#tarifs" className="mobile-nav-link" onClick={() => setMenuOpen(false)}>Tarifs <span style={{fontSize:'10px',color:'rgba(255,255,255,0.3)',marginLeft:'6px'}}>bientôt</span></a>
              <a href="#temoignages" className="mobile-nav-link" onClick={() => setMenuOpen(false)}>Témoignages <span style={{fontSize:'10px',color:'rgba(255,255,255,0.3)',marginLeft:'6px'}}>bientôt</span></a>
            </nav>
            <div className="mobile-menu-actions">
              <button
                className="mobile-menu-cliente-btn"
                onClick={() => { setMenuOpen(false); setShowClienteModal(true); }}
                data-testid="mobile-acces-cliente-btn"
              >
                🛍️ Accès cliente
              </button>
              <button
                className="mobile-menu-login-btn"
                onClick={() => { setMenuOpen(false); setShowLoginModal(true); }}
                data-testid="mobile-se-connecter-btn"
              >
                Se connecter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <main className="hero">
        {/* Badge */}
        <div className="badge hero-badge">
          Gestion boutique intelligente
        </div>

        {/* Title */}
        <h1 className="title hero-title">
          <span className="title-light">Votre boutique.</span>
          <span className="title-bold">Sans limites.</span>
        </h1>

        {/* Subtitle */}
        <p className="subtitle hero-subtitle">
          Gérez vos stocks, fidélisez vos clientes et boostez vos ventes — 
          depuis une seule interface, conçue pour les boutiques modernes.
        </p>

        {/* CTA Buttons */}
        <div className="cta-row hero-cta">
          <button className="cta-btn" onClick={() => navigate("/onboarding")} data-testid="cta-start-free">
            Commencer gratuitement →
          </button>
          <button className="cta-btn-outline" onClick={() => document.querySelector('.stats-band')?.scrollIntoView({ behavior: 'smooth' })} data-testid="cta-see-demo">
            Voir la démo
          </button>
        </div>

        {/* Trust Note */}
        <p className="trust-note hero-trust">
          AUCUNE CARTE BANCAIRE REQUISE · DONNÉES SÉCURISÉES
        </p>

        {/* [MODALES-ACCES] Secondary link — opens login modal */}
        <button
          onClick={() => setShowLoginModal(true)}
          className="secondary-link hero-secondary"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="secondary-login-link"
        >
          Déjà une boutique ? Se connecter
        </button>
      </main>

      {/* Stats Band */}
      <footer className="stats-band">
        <div className="stats-content">
          <div className="stat">
            <span className="stat-value">+500</span>
            <span className="stat-label">Boutiques actives</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-value">POS</span>
            <span className="stat-label">Scan code-barres</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-value">PDF</span>
            <span className="stat-label">Factures auto</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-value">24/7</span>
            <span className="stat-label">Support inclus</span>
          </div>
        </div>
        <a
          href="/super-admin"
          className="admin-badge"
          data-testid="superadmin-footer-link"
        >
          Admin
        </a>
      </footer>

      {/* ─── [MODALES-ACCES] Modale 1 — Connexion multi-rôles ─── */}
      {showLoginModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowLoginModal(false)}
          data-testid="login-modal-overlay"
        >
          <div
            className={`modal-card${shakeLogin ? ' modal-shake' : ''}`}
            style={{ animation: 'modalEnter 300ms ease forwards' }}
            onClick={e => e.stopPropagation()}
            data-testid="login-modal"
          >
            {/* Close */}
            <button
              className="modal-close"
              onClick={() => setShowLoginModal(false)}
              data-testid="login-modal-close"
            >✕</button>

            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
              <span style={{ color: 'white', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.02em', fontFamily: "'DM Sans', sans-serif" }}>BoutiqueOS</span>
            </div>

            {/* ══ STEP: SEARCH ══ */}
            {loginStep === "search" && (
              <>
                <h2 className="modal-title">Accéder à ma boutique</h2>
                <p className="modal-subtitle">Email ou nom de votre boutique</p>

                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    autoComplete="off"
                    className={`modal-input${checkResult?.found === false ? ' input-error' : checkResult?.found ? ' input-success' : ''}`}
                    placeholder="Email ou nom de boutique"
                    value={identifiant}
                    onChange={e => {
                      setIdentifiant(e.target.value);
                      setLoginError("");
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && checkResult?.found) setLoginStep("role");
                    }}
                    autoFocus
                    data-testid="login-identifiant-input"
                  />

                  {checking && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>Recherche...</div>
                  )}

                  {!checking && checkResult?.found && (
                    <div className="boutique-found-card" data-testid="boutique-found-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="boutique-initials-badge">
                          {getInitials(checkResult.boutique_name)}
                        </div>
                        <div>
                          <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>{checkResult.boutique_name}</div>
                          {checkResult.boutique_type && (
                            <div style={{ color: '#9CA3AF', fontSize: '12px' }}>{checkResult.boutique_type}</div>
                          )}
                        </div>
                      </div>
                      <span className="found-badge" data-testid="boutique-found-badge">Trouvée</span>
                    </div>
                  )}

                  {!checking && identifiant.length >= 2 && checkResult?.found === false && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#FCA5A5' }} data-testid="boutique-not-found">
                      Boutique introuvable
                    </div>
                  )}
                </div>

                <button
                  className="modal-submit-btn"
                  onClick={() => setLoginStep("role")}
                  disabled={!checkResult?.found}
                  data-testid="login-continue-btn"
                >
                  Continuer →
                </button>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    onClick={() => { setShowLoginModal(false); navigate('/onboarding'); }}
                    style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: '13px', cursor: 'pointer' }}
                    data-testid="create-boutique-link"
                  >
                    Pas encore de boutique ? Créer la mienne →
                  </button>
                </div>
              </>
            )}

            {/* ══ STEP: ROLE SELECTION ══ */}
            {loginStep === "role" && (
              <>
                <h2 className="modal-title">Comment vous connecter ?</h2>
                <p className="modal-subtitle">{checkResult?.boutique_name}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {/* Propriétaire */}
                  <button
                    className="role-card"
                    onClick={() => { setSelectedRole("owner"); setLoginStep("form"); setPassword(""); setLoginError(""); }}
                    data-testid="role-owner-btn"
                  >
                    <div className="role-icon" style={{ background: 'rgba(212,175,55,0.12)', color: '#D4AF37' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7z"/><path d="M3 20h18"/></svg>
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>Proprietaire</div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>Acces complet a la boutique</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>

                  {/* Vendeur */}
                  <button
                    className="role-card"
                    onClick={() => { setSelectedRole("team"); setLoginStep("form"); setPassword(""); setTeamEmail(""); setLoginError(""); }}
                    data-testid="role-team-btn"
                  >
                    <div className="role-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>Vendeur / Equipe</div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>Connexion avec vos identifiants</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>

                <button
                  className="modal-back-btn"
                  onClick={() => { setLoginStep("search"); setSelectedRole(null); }}
                  data-testid="login-back-to-search"
                >
                  ← Retour
                </button>
              </>
            )}

            {/* ══ STEP: LOGIN FORM ══ */}
            {loginStep === "form" && (
              <>
                <h2 className="modal-title">
                  {selectedRole === "owner" ? "Connexion Proprietaire" : "Connexion Equipe"}
                </h2>
                <p className="modal-subtitle">{checkResult?.boutique_name}</p>

                {/* Team email field */}
                {selectedRole === "team" && (
                  <div className="password-fade-in" style={{ marginBottom: '12px' }}>
                    <input
                      type="email"
                      className="modal-input"
                      placeholder="Votre adresse email"
                      value={teamEmail}
                      autoFocus
                      onChange={e => { setTeamEmail(e.target.value); setLoginError(""); }}
                      onKeyDown={e => { if (e.key === 'Enter') document.getElementById('modal-password-input')?.focus(); }}
                      data-testid="login-team-email-input"
                    />
                  </div>
                )}

                {/* Password field */}
                <div className="password-fade-in" style={{ marginBottom: '12px' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="modal-password-input"
                      type={showPassword ? "text" : "password"}
                      className="modal-input"
                      placeholder="Mot de passe"
                      value={password}
                      autoFocus={selectedRole === "owner"}
                      onChange={e => { setPassword(e.target.value); setLoginError(""); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '15px', padding: '0' }}
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {loginError && (
                  <div style={{ color: '#FCA5A5', fontSize: '12px', marginBottom: '12px', textAlign: 'center' }} data-testid="login-error">
                    {loginError}
                  </div>
                )}

                {/* Submit */}
                <button
                  className="modal-submit-btn"
                  onClick={handleLogin}
                  disabled={loginLoading}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? 'Connexion...' : 'Se connecter →'}
                </button>

                <button
                  className="modal-back-btn"
                  onClick={() => { setLoginStep("role"); setLoginError(""); setPassword(""); }}
                  data-testid="login-back-to-role"
                >
                  ← Retour
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── [MODALES-ACCES] Modale 2 — Accès Cliente ─── */}
      {showClienteModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowClienteModal(false)}
          data-testid="cliente-modal-overlay"
        >
          <div
            className={`modal-card${codeInvalid ? ' modal-shake' : ''}`}
            style={{ animation: 'modalEnter 300ms ease forwards' }}
            onClick={e => e.stopPropagation()}
            data-testid="cliente-modal"
          >
            {/* Close */}
            <button
              className="modal-close"
              onClick={() => setShowClienteModal(false)}
              data-testid="cliente-modal-close"
            >✕</button>

            {/* Shopping bag icon */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 01-8 0"/>
                </svg>
              </div>
            </div>

            <h2 className="modal-title">Accéder à une boutique</h2>
            <p className="modal-subtitle" style={{ marginBottom: '28px' }}>
              Entrez le code donné par votre gérante
            </p>

            {/* 6 code boxes */}
            <div
              style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}
              onPaste={handleCodePaste}
              data-testid="code-input-group"
            >
              {codeDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={el => { codeRefs.current[index] = el; }}
                  type="text"
                  inputMode="text"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(index, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(index, e)}
                  className={`code-box${codeLoading ? ' code-loading' : ''}${codeInvalid ? ' code-invalid' : ''}${digit ? ' code-filled' : ''}`}
                  data-testid={`code-box-${index}`}
                />
              ))}
            </div>

            {codeLoading && (
              <div style={{ textAlign: 'center', color: '#6B7280', fontSize: '13px', margin: '8px 0' }}>
                Vérification...
              </div>
            )}

            {!codeLoading && codeResult && (
              <div className="cliente-boutique-card" data-testid="cliente-boutique-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <div className="boutique-initials-badge" style={{ background: 'rgba(255,69,0,0.1)', color: '#FF4500', border: '1px solid rgba(255,69,0,0.2)' }}>
                    {getInitials(codeResult.boutique_name)}
                  </div>
                  <div>
                    <div style={{ color: 'white', fontWeight: '700', fontSize: '15px' }}>{codeResult.boutique_name}</div>
                    {codeResult.boutique_type && (
                      <div style={{ color: '#9CA3AF', fontSize: '12px' }}>{codeResult.boutique_type}</div>
                    )}
                  </div>
                </div>
                <span className="found-badge" style={{ fontSize: '12px', marginBottom: '12px', display: 'inline-block' }}>Boutique trouvée ✓</span>
                <button
                  className="cliente-access-btn"
                  onClick={handleClienteAccess}
                  data-testid="cliente-access-btn"
                >
                  Accéder →
                </button>
              </div>
            )}

            {!codeLoading && codeInvalid && (
              <div style={{ textAlign: 'center', color: '#FCA5A5', fontSize: '13px', marginTop: '8px' }} data-testid="code-error-invalid">
                Code incorrect.
              </div>
            )}

            {!codeLoading && codeNotAvailable && (
              <div style={{ textAlign: 'center', color: '#FCD34D', fontSize: '13px', marginTop: '8px' }} data-testid="code-error-unavailable">
                Cette boutique n'est pas encore disponible.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Modale 3 — Onboarding Cliente ─── */}
      {showClienteOnboarding && (
        <div
          className="modal-overlay"
          onClick={() => setShowClienteOnboarding(false)}
          data-testid="onboarding-modal-overlay"
        >
          <div
            className="modal-card"
            style={{ animation: 'modalEnter 300ms ease forwards' }}
            onClick={e => e.stopPropagation()}
            data-testid="onboarding-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowClienteOnboarding(false)}
              data-testid="onboarding-modal-close"
            >✕</button>

            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
            </div>

            <h2 className="modal-title">Bienvenue !</h2>
            <p className="modal-subtitle" style={{ marginBottom: '24px' }}>
              Entrez vos informations pour accéder à la boutique<br />
              <strong style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                {pendingBoutiqueRef.current?.boutique_name}
              </strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#9CA3AF', display: 'block', marginBottom: '6px', fontFamily: "'DM Sans', sans-serif" }}>
                  Votre prénom *
                </label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="Ex : Aminata"
                  value={clientePrenom}
                  onChange={e => { setClientePrenom(e.target.value); setOnboardingError(""); }}
                  onKeyDown={e => { if (e.key === 'Enter') document.getElementById('onboarding-tel')?.focus(); }}
                  autoFocus
                  data-testid="onboarding-prenom-input"
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#9CA3AF', display: 'block', marginBottom: '6px', fontFamily: "'DM Sans', sans-serif" }}>
                  WhatsApp / Téléphone <span style={{ color: '#6B7280' }}>(optionnel)</span>
                </label>
                <input
                  id="onboarding-tel"
                  type="tel"
                  className="modal-input"
                  placeholder="Ex : +225 07 00 00 00 00"
                  value={clienteTel}
                  onChange={e => setClienteTel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleClienteOnboardingSubmit(); }}
                  data-testid="onboarding-tel-input"
                />
              </div>
            </div>

            {onboardingError && (
              <div style={{ color: '#FCA5A5', fontSize: '12px', marginBottom: '12px', textAlign: 'center' }}>
                {onboardingError}
              </div>
            )}

            <button
              className="modal-submit-btn"
              onClick={handleClienteOnboardingSubmit}
              data-testid="onboarding-submit-btn"
            >
              Accéder à la boutique →
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* [REDESIGN-LANDING] ─── Design System Tokens ─────────────────────── */
        :root {
          --bg:           #0A0A0A;
          --bg-card:      #111111;
          --bg-hover:     #1A1A1A;
          --border:       #2A2A2A;
          --border-hover: #3A3A3A;
          --text:         #FFFFFF;
          --text-2:       #9CA3AF;
          --text-3:       #6B7280;
          --accent:       #FF4500;
          --accent-dim:   rgba(255,69,0,0.12);
          --accent-ring:  rgba(255,69,0,0.20);
          --success:      #22C55E;
          --error:        #EF4444;
          --font:         'Inter', system-ui, -apple-system, sans-serif;
          --mono:         'JetBrains Mono', 'Courier New', monospace;
          --ease:         cubic-bezier(0.4,0,0.2,1);
          --ease-out:     cubic-bezier(0,0,0.2,1);
          --ease-bounce:  cubic-bezier(0.34,1.56,0.64,1);
        }

        /* ─── Page container ─────────────────────────────────────────────── */
        .boutiqueos-landing {
          min-height: 100vh;
          height: 100vh;
          overflow: hidden;
          background: var(--bg);
          display: flex;
          flex-direction: column;
          font-family: var(--font);
          position: relative;
        }

        /* ─── Background video & overlay ───────────────────────────────── */
        .bg-video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
          pointer-events: none;
        }
        .bg-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.75);
          z-index: 0;
          pointer-events: none;
        }

        /* ─── Navbar ─────────────────────────────────────────────────────── */
        .navbar {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          height: 60px;
          display: flex;
          align-items: center;
          border-bottom: 1px solid var(--border);
          background: rgba(10,10,10,0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .navbar-pill {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .navbar-left {
          flex-shrink: 0;
          min-width: 140px;
        }

        .navbar-logo {
          font-family: var(--font);
          font-size: 15px;
          font-weight: 600;
          color: var(--text);
          letter-spacing: -0.02em;
          cursor: pointer;
          user-select: none;
          display: inline-block;
        }

        .navbar-links {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
        }

        .nav-link {
          font-family: var(--font);
          font-size: 13px;
          font-weight: 400;
          color: var(--text-2);
          text-decoration: none;
          padding: 6px 12px;
          border-radius: 8px;
          transition: color 150ms var(--ease), background 150ms var(--ease);
          position: relative;
          white-space: nowrap;
        }
        .nav-link:hover { color: var(--text); background: var(--bg-hover); }
        .nav-link.active { color: var(--text); }

        /* "Bientôt" tooltip */
        .nav-link-soon { cursor: default; }
        .nav-link-soon::after {
          content: attr(data-soon);
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: var(--bg-card);
          border: 1px solid var(--border);
          color: var(--text-3);
          font-size: 11px;
          font-family: var(--font);
          padding: 3px 8px;
          border-radius: 6px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 150ms var(--ease);
        }
        .nav-link-soon:hover { color: var(--text-3) !important; background: transparent !important; }
        .nav-link-soon:hover::after { opacity: 1; }

        .navbar-actions {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 140px;
          justify-content: flex-end;
        }

        .navbar-cliente-btn {
          font-family: var(--font);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-2);
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 0 14px;
          min-height: 44px;
          cursor: pointer;
          transition: color 150ms var(--ease), border-color 150ms var(--ease), background 150ms var(--ease);
          white-space: nowrap;
        }
        .navbar-cliente-btn:hover {
          color: var(--text);
          border-color: var(--border-hover);
          background: var(--bg-hover);
        }

        .navbar-login-btn {
          font-family: var(--font);
          font-size: 13px;
          font-weight: 600;
          color: #0A0A0A;
          background: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 0 16px;
          min-height: 44px;
          cursor: pointer;
          transition: opacity 150ms var(--ease), transform 150ms var(--ease);
          white-space: nowrap;
        }
        .navbar-login-btn:hover { opacity: 0.88; }
        .navbar-login-btn:active { transform: scale(0.97); }

        /* ─── Hero ───────────────────────────────────────────────────────── */
        .hero {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 160px 24px 0;
          position: relative;
          z-index: 1;
        }

        /* ─── Badge ──────────────────────────────────────────────────────── */
        .badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: var(--mono);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.05em;
          color: var(--accent);
          background: var(--accent-dim);
          border: 1px solid rgba(255,69,0,0.25);
          padding: 5px 14px;
          border-radius: 20px;
          text-transform: uppercase;
          margin-bottom: 28px;
          position: relative;
          z-index: 1;
        }
        .badge::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--accent);
          display: inline-block;
          animation: pulse-dot 2s ease infinite;
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }

        /* ─── Title ──────────────────────────────────────────────────────── */
        .title {
          font-family: var(--font);
          font-size: clamp(40px, 6vw, 72px);
          font-weight: 700;
          line-height: 1.08;
          letter-spacing: -0.03em;
          color: var(--text);
          margin: 0 0 20px;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .title-light {
          color: var(--text-2);
          font-weight: 300;
          display: block;
        }
        .title-bold {
          color: var(--text);
          font-weight: 700;
          display: block;
        }

        /* ─── Subtitle ───────────────────────────────────────────────────── */
        .subtitle {
          font-family: var(--font);
          font-size: 16px;
          font-weight: 400;
          line-height: 1.6;
          color: var(--text-3);
          max-width: 480px;
          margin: 0 0 36px;
          position: relative;
          z-index: 1;
        }

        /* ─── CTA Buttons ────────────────────────────────────────────────── */
        .cta-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .cta-btn {
          font-family: var(--font);
          font-size: 15px;
          font-weight: 600;
          color: #0A0A0A;
          background: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 0 28px;
          height: 52px;
          cursor: pointer;
          transition: opacity 150ms var(--ease), transform 150ms var(--ease);
          letter-spacing: -0.01em;
        }
        .cta-btn:hover { opacity: 0.88; }
        .cta-btn:active { transform: scale(0.97); }

        .cta-btn-outline {
          font-family: var(--font);
          font-size: 15px;
          font-weight: 600;
          color: #FF4500;
          background: transparent;
          border: 1px solid rgba(255,69,0,0.5);
          border-radius: 10px;
          padding: 0 28px;
          height: 52px;
          cursor: pointer;
          transition: background 150ms var(--ease), border-color 150ms var(--ease), transform 150ms var(--ease);
          letter-spacing: -0.01em;
        }
        .cta-btn-outline:hover {
          background: rgba(255,69,0,0.08);
          border-color: #FF4500;
        }
        .cta-btn-outline:active { transform: scale(0.97); }

        /* ─── Trust note ─────────────────────────────────────────────────── */
        .trust-note {
          font-family: var(--mono);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.06em;
          color: var(--text-3);
          text-transform: uppercase;
          margin: 0 0 20px;
          position: relative;
          z-index: 1;
        }

        /* ─── Secondary link ─────────────────────────────────────────────── */
        .secondary-link {
          font-family: var(--font);
          font-size: 13px;
          font-weight: 400;
          color: var(--text-3);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin: 0;
          position: relative;
          z-index: 1;
          transition: color 150ms var(--ease);
          text-decoration: underline;
          text-decoration-color: rgba(107,114,128,0.4);
          text-underline-offset: 3px;
        }
        .secondary-link:hover {
          color: var(--text-2);
          text-decoration-color: var(--text-2);
        }

        /* ─── Staggered entry animations ─────────────────────────────────── */
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-badge    { animation: heroFadeUp 400ms var(--ease-out) 0.05s both; }
        .hero-title    { animation: heroFadeUp 400ms var(--ease-out) 0.15s both; }
        .hero-subtitle { animation: heroFadeUp 400ms var(--ease-out) 0.22s both; }
        .hero-cta      { animation: heroFadeUp 400ms var(--ease-out) 0.29s both; }
        .hero-trust    { animation: heroFadeUp 400ms var(--ease-out) 0.34s both; }
        .hero-secondary{ animation: heroFadeUp 400ms var(--ease-out) 0.39s both; }

        /* ─── Stats Band ─────────────────────────────────────────────────── */
        .stats-band {
          position: relative;
          border-top: 1px solid var(--border);
          background: var(--bg);
          z-index: 1;
        }

        .stats-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 0;
        }

        .stat {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          padding: 8px 0;
        }

        .stat-value {
          font-family: var(--mono);
          font-size: 15px;
          font-weight: 500;
          color: var(--text);
          letter-spacing: -0.01em;
        }

        .stat-label {
          font-family: var(--font);
          font-size: 11px;
          font-weight: 400;
          color: var(--text-3);
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        .stat-divider {
          width: 1px;
          height: 32px;
          background: var(--border);
          flex-shrink: 0;
        }

        /* ─── Admin badge ───────────────────────────────────────────────── */
        .admin-badge {
          position: absolute;
          left: 16px;
          bottom: 8px;
          font-family: var(--mono);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 3px 10px;
          border-radius: 6px;
          transition: color 150ms, background 150ms, border-color 150ms;
        }
        .admin-badge:hover {
          color: var(--accent);
          background: rgba(255,69,0,0.08);
          border-color: rgba(255,69,0,0.3);
        }

        /* ─── Modal overlay ──────────────────────────────────────────────── */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: overlayIn 200ms var(--ease-out) both;
        }
        @keyframes overlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .modal-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 28px 28px 24px;
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 1000;
        }
        @keyframes modalEnter {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }

        .modal-shake {
          animation: modalShake 0.5s var(--ease) both !important;
        }
        @keyframes modalShake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }

        .modal-close {
          position: absolute;
          top: 16px; right: 16px;
          width: 28px; height: 28px;
          border-radius: 8px;
          background: var(--bg-hover);
          border: 1px solid var(--border);
          color: var(--text-3);
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 150ms var(--ease), color 150ms var(--ease);
          line-height: 1;
        }
        .modal-close:hover { background: var(--border); color: var(--text); }

        .modal-title {
          font-family: var(--font);
          font-size: 18px;
          font-weight: 600;
          color: var(--text);
          letter-spacing: -0.02em;
          margin: 0 0 4px;
          text-align: center;
        }

        .modal-subtitle {
          font-family: var(--font);
          font-size: 13px;
          font-weight: 400;
          color: var(--text-3);
          text-align: center;
          margin: 0 0 20px;
          line-height: 1.5;
        }

        /* ─── Modal inputs ───────────────────────────────────────────────── */
        .modal-input {
          width: 100%;
          box-sizing: border-box;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 11px 14px;
          font-family: var(--font);
          font-size: 14px;
          color: var(--text);
          outline: none;
          transition: border-color 150ms var(--ease), box-shadow 150ms var(--ease);
          -webkit-appearance: none;
        }
        .modal-input::placeholder { color: var(--text-3); }
        .modal-input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-ring);
        }
        .modal-input.input-success {
          border-color: var(--success);
          box-shadow: 0 0 0 3px rgba(34,197,94,0.12);
        }
        .modal-input.input-error {
          border-color: var(--error);
          box-shadow: 0 0 0 3px rgba(239,68,68,0.12);
        }

        /* ─── Boutique found card ────────────────────────────────────────── */
        .boutique-found-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--bg-hover);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 10px 12px;
          margin-top: 8px;
          animation: heroFadeUp 200ms var(--ease-out) both;
        }

        .boutique-initials-badge {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: var(--accent-dim);
          color: var(--accent);
          border: 1px solid rgba(255,69,0,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font);
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .found-badge {
          font-family: var(--mono);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.03em;
          color: var(--success);
          background: rgba(34,197,94,0.1);
          border: 1px solid rgba(34,197,94,0.2);
          padding: 3px 8px;
          border-radius: 20px;
          flex-shrink: 0;
        }

        /* ─── Password fade-in ───────────────────────────────────────────── */
        .password-fade-in {
          animation: heroFadeUp 250ms var(--ease-out) both;
        }

        /* ─── Role cards ──────────────────────────────────────────────────── */
        .role-card {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          cursor: pointer;
          font-family: var(--font);
          transition: border-color 150ms var(--ease), background 150ms var(--ease);
        }
        .role-card:hover {
          border-color: var(--border-hover);
          background: var(--bg-hover);
        }
        .role-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* ─── Modal back button ──────────────────────────────────────────── */
        .modal-back-btn {
          display: block;
          width: 100%;
          margin-top: 12px;
          padding: 10px;
          background: none;
          border: none;
          color: var(--text-3);
          font-family: var(--font);
          font-size: 13px;
          cursor: pointer;
          text-align: center;
          transition: color 150ms var(--ease);
        }
        .modal-back-btn:hover { color: var(--text-2); }

        /* ─── Submit button ──────────────────────────────────────────────── */
        .modal-submit-btn {
          width: 100%;
          background: #FFFFFF;
          color: #0A0A0A;
          font-family: var(--font);
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          height: 44px;
          cursor: pointer;
          transition: opacity 150ms var(--ease), transform 150ms var(--ease);
          letter-spacing: -0.01em;
        }
        .modal-submit-btn:hover:not(:disabled) { opacity: 0.88; }
        .modal-submit-btn:active:not(:disabled) { transform: scale(0.98); }
        .modal-submit-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        /* ─── Cliente boutique result card ───────────────────────────────── */
        .cliente-boutique-card {
          background: var(--bg-hover);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          margin-top: 8px;
          animation: heroFadeUp 200ms var(--ease-out) both;
        }

        .cliente-access-btn {
          width: 100%;
          background: #FFFFFF;
          color: #0A0A0A;
          font-family: var(--font);
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          height: 44px;
          cursor: pointer;
          transition: opacity 150ms var(--ease), transform 150ms var(--ease);
        }
        .cliente-access-btn:hover { opacity: 0.88; }
        .cliente-access-btn:active { transform: scale(0.98); }

        /* ─── Code boxes ─────────────────────────────────────────────────── */
        .code-box {
          width: 44px;
          height: 52px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 10px;
          text-align: center;
          font-family: var(--mono);
          font-size: 18px;
          font-weight: 500;
          color: var(--text);
          caret-color: var(--accent);
          outline: none;
          transition: border-color 150ms var(--ease), box-shadow 150ms var(--ease);
          -webkit-appearance: none;
        }
        .code-box:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-ring);
        }
        .code-box.code-filled {
          border-color: var(--border-hover);
          background: var(--bg-hover);
        }
        .code-box.code-loading { opacity: 0.5; }
        .code-box.code-invalid {
          border-color: var(--error);
          box-shadow: 0 0 0 3px rgba(239,68,68,0.12);
          animation: modalShake 0.5s var(--ease) both;
        }

        /* ─── Hamburger ──────────────────────────────────────────────────── */
        .navbar-hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          padding: 0;
          background: none;
          border: 1px solid var(--border);
          border-radius: 8px;
          cursor: pointer;
          min-width: 44px;
          min-height: 44px;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: border-color 150ms var(--ease), background 150ms var(--ease);
        }
        .navbar-hamburger:hover { background: var(--bg-hover); border-color: var(--border-hover); }

        .ham-bar {
          display: block;
          width: 18px; height: 1.5px;
          background: var(--text-2);
          border-radius: 1px;
          transition: transform 250ms var(--ease), opacity 200ms var(--ease);
          transform-origin: center;
        }
        .ham-bar.open-1 { transform: translateY(6.5px) rotate(45deg); }
        .ham-bar.open-2 { opacity: 0; transform: scaleX(0); }
        .ham-bar.open-3 { transform: translateY(-6.5px) rotate(-45deg); }

        /* ─── Mobile menu ────────────────────────────────────────────────── */
        .mobile-menu-overlay {
          display: none;
          position: fixed;
          inset: 0;
          z-index: 998;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          animation: overlayIn 200ms var(--ease-out) both;
        }

        .mobile-menu-panel {
          position: absolute;
          top: 0; left: 0; right: 0;
          background: var(--bg-card);
          border-bottom: 1px solid var(--border);
          padding: 72px 24px 24px;
          animation: panelSlideDown 300ms var(--ease-out) both;
        }
        @keyframes panelSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }

        .mobile-menu-links {
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
          border-bottom: 1px solid var(--border);
          padding-bottom: 16px;
        }
        .mobile-nav-link {
          font-family: var(--font);
          font-size: 16px;
          font-weight: 500;
          color: var(--text-2);
          text-decoration: none;
          padding: 12px 0;
          min-height: 44px;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 1px solid var(--border);
          transition: color 150ms var(--ease);
        }
        .mobile-nav-link:last-child { border-bottom: none; }
        .mobile-nav-link:hover { color: var(--text); }

        .mobile-menu-actions { display: flex; flex-direction: column; gap: 8px; }

        .mobile-menu-cliente-btn {
          width: 100%;
          min-height: 52px;
          border-radius: 10px;
          background: var(--bg-hover);
          border: 1px solid var(--border);
          color: var(--text-2);
          font-family: var(--font);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 150ms var(--ease), border-color 150ms var(--ease), color 150ms var(--ease);
        }
        .mobile-menu-cliente-btn:hover { background: var(--border); color: var(--text); }

        .mobile-menu-login-btn {
          width: 100%;
          min-height: 52px;
          border-radius: 10px;
          background: #FFFFFF;
          border: none;
          color: #0A0A0A;
          font-family: var(--font);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 150ms var(--ease);
        }
        .mobile-menu-login-btn:hover { opacity: 0.88; }

        /* ─── Responsive ─────────────────────────────────────────────────── */
        @media (max-width: 768px) {
          .navbar-hamburger { display: flex; }
          .navbar-actions   { display: none !important; }
          .navbar-links     { display: none !important; }
          .mobile-menu-overlay { display: block; }
          .hero { padding-top: 120px; }
          .subtitle { font-size: 15px; }
          .stats-content { gap: 0; padding: 16px 16px; }
          .stat-label { font-size: 10px; }
        }

        @media (max-width: 480px) {
          .hero { padding-top: 100px; }
          .modal-card { padding: 24px 20px 20px; }
          .code-box { width: 38px; height: 46px; font-size: 16px; }
          .stats-content { flex-wrap: wrap; }
          .stat { flex: 0 0 50%; padding: 10px 0; }
          .stat-divider { display: none; }
          .cta-row { flex-direction: column; width: 100%; max-width: 300px; }
          .cta-btn, .cta-btn-outline { width: 100%; }
        }

        @media (min-width: 769px) and (max-width: 1100px) {
          .navbar-links { gap: 0; }
          .hero { padding-top: 140px; }
        }
      `}</style>
    </div>
  );
}

